import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff, Lock, CheckCircle, AlertCircle, X } from 'lucide-react';
import { supabase } from '../lib/supabase';

const resetPasswordSchema = z.object({
  password: z.string().min(8, 'Le mot de passe doit contenir au moins 8 caractères'),
  confirmPassword: z.string().min(8, 'Le mot de passe doit contenir au moins 8 caractères'),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Les mots de passe ne correspondent pas",
  path: ["confirmPassword"],
});

type ResetPasswordForm = z.infer<typeof resetPasswordSchema>;

// Délai d'attente de l'événement PASSWORD_RECOVERY avant de déclarer le lien invalide
const RECOVERY_EVENT_TIMEOUT_MS = 4000;

interface Notification {
  type: 'success' | 'error';
  message: string;
}

export default function ResetPassword() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState<Notification | null>(null);
  const [isValidToken, setIsValidToken] = useState(false);
  const [isCheckingToken, setIsCheckingToken] = useState(true);

  const form = useForm<ResetPasswordForm>({
    resolver: zodResolver(resetPasswordSchema),
  });

  useEffect(() => {
    let mounted = true;

    // Le formulaire n'est activé que par l'événement PASSWORD_RECOVERY, émis par
    // Supabase quand l'URL contient un lien de récupération. Une simple session
    // existante (utilisateur déjà connecté, session volée) ne suffit pas : elle
    // permettrait de changer le mot de passe sans connaître l'ancien.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (!mounted) return;

      if (event === 'PASSWORD_RECOVERY') {
        clearTimeout(timeoutId);
        setIsValidToken(true);
        setIsCheckingToken(false);
      }
    });

    // Sans lien de récupération, l'événement n'arrive jamais : on conclut à un lien invalide.
    const timeoutId = setTimeout(() => {
      if (!mounted) return;
      setNotification({
        type: 'error',
        message: 'Lien de réinitialisation invalide ou expiré. Demandez un nouveau lien depuis la page de connexion.'
      });
      setIsCheckingToken(false);
    }, RECOVERY_EVENT_TIMEOUT_MS);

    // Cleanup
    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      subscription.unsubscribe();
    };
  }, []);

  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
  };

  const hideNotification = () => {
    setNotification(null);
  };

  const handleResetPassword = async (data: ResetPasswordForm) => {
    setLoading(true);
    setNotification(null);

    try {
      // Début de la mise à jour du mot de passe
      const { error } = await supabase.auth.updateUser({
        password: data.password
      });

      if (error) {
        console.error('Erreur Supabase:', error);
        showNotification('error', `Erreur lors de la mise à jour du mot de passe: ${error.message}`);
      } else {
        showNotification('success', 'Mot de passe mis à jour avec succès ! Redirection vers la connexion...');

        // Déconnecter l'utilisateur après la réinitialisation
        const { error: signOutError } = await supabase.auth.signOut();

        if (signOutError) {
          console.error('Erreur lors de la déconnexion:', signOutError);
          showNotification('error', 'Erreur lors de la déconnexion. Veuillez vous déconnecter manuellement.');
        }

        // Attendre un peu avant la redirection pour que l'utilisateur puisse voir le message
        setTimeout(() => {
          // Utiliser window.location pour forcer la redirection dans le même onglet
          window.location.href = '/login';
        }, 2000); // Réduit à 2 secondes
      }
    } catch (error) {
      console.error('Erreur inattendue:', error);
      showNotification('error', 'Une erreur inattendue est survenue.');
    } finally {
      setLoading(false);
    }
  };

  // Affichage de chargement pendant la vérification du token
  if (isCheckingToken) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center p-4">
        <div className="bg-white/10 dark:bg-gray-900/10 backdrop-blur-lg rounded-2xl border border-white/20 p-8 max-w-md mx-auto">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
            <p className="text-white">Vérification du lien de réinitialisation...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!isValidToken && notification) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center p-4">
        <div className="bg-white/10 dark:bg-gray-900/10 backdrop-blur-lg rounded-2xl border border-white/20 p-8 max-w-md mx-auto">
          <div className="text-center mb-6">
            <h2 className="text-3xl font-bold text-white mb-2">
              Erreur de validation
            </h2>
          </div>

          <div className="bg-red-100/80 border-red-300 border-2 text-red-800 backdrop-blur-sm p-4 rounded-xl">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
              <span className="text-sm font-medium">{notification.message}</span>
            </div>
          </div>

          <div className="mt-6 text-center">
            <button
              onClick={() => navigate('/login')}
              className="bg-gradient-to-r from-purple-600 to-pink-600 text-white py-2 px-4 rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all duration-200"
            >
              Demander un nouveau lien
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center p-4">
      <div className="bg-white/10 dark:bg-gray-900/10 backdrop-blur-lg rounded-2xl border border-white/20 p-8 max-w-md mx-auto">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-white mb-2">
            Nouveau mot de passe
          </h2>
          <p className="text-gray-300">
            Choisissez un nouveau mot de passe sécurisé
          </p>
        </div>

        {isValidToken && (
          <form onSubmit={form.handleSubmit(handleResetPassword)} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Nouveau mot de passe
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  {...form.register('password')}
                  type={showPassword ? 'text' : 'password'}
                  className="w-full pl-10 pr-12 py-3 bg-white/50 dark:bg-gray-800/50 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-white"
                  placeholder="Votre nouveau mot de passe"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              {form.formState.errors.password && (
                <p className="mt-1 text-sm text-red-400">{form.formState.errors.password.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Confirmer le mot de passe
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  {...form.register('confirmPassword')}
                  type={showConfirmPassword ? 'text' : 'password'}
                  className="w-full pl-10 pr-12 py-3 bg-white/50 dark:bg-gray-800/50 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-white"
                  placeholder="Confirmez votre mot de passe"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              {form.formState.errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-400">{form.formState.errors.confirmPassword.message}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 px-6 rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Mise à jour...' : 'Mettre à jour le mot de passe'}
            </button>

            {/* Messages de notification juste sous le formulaire */}
            {notification && (
              <div className={`mt-4 p-4 rounded-xl border-2 ${notification.type === 'success'
                  ? 'bg-green-100/80 border-green-300 text-green-800 backdrop-blur-sm'
                  : 'bg-red-100/80 border-red-300 text-red-800 backdrop-blur-sm'
                } relative`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center">
                    {notification.type === 'success' ? (
                      <CheckCircle className="h-5 w-5 mr-2 flex-shrink-0" />
                    ) : (
                      <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
                    )}
                    <span className="text-sm font-medium">{notification.message}</span>
                  </div>
                  <button
                    onClick={hideNotification}
                    className={`ml-4 flex-shrink-0 ${notification.type === 'success' ? 'text-green-600 hover:text-green-800' : 'text-red-600 hover:text-red-800'
                      }`}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </form>
        )}

        <div className="mt-8 text-center">
          <button
            onClick={() => navigate('/login')}
            className="text-purple-400 hover:text-purple-300 text-sm"
          >
            Retour à la connexion
          </button>
        </div>
      </div>
    </div>
  );
}
